import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../utils/supabase';
import { createSystemAlert, resolveSystemAlert } from '../../../utils/dashboardService';

const QuickActions = () => {
  const navigate = useNavigate();
  const [actionCounts, setActionCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActionCounts();
  }, []);

  const fetchActionCounts = async () => {
    try {
      // Get pending games count
      const { data: pendingGames } = await supabase
        .from('games')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'under_review');

      // Get urgent tickets count
      const { data: urgentTickets } = await supabase
        .from('contacts')
        .select('id', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress'])
        .eq('priority', 'urgent');

      // Get failed payments count
      const { data: failedPayments } = await supabase
        .from('subscriptions')
        .select('id', { count: 'exact', head: true })
        .in('status', ['past_due', 'unpaid']);

      // Get unresolved alerts count
      const { data: unresolvedAlerts } = await supabase
        .from('system_alerts')
        .select('id', { count: 'exact', head: true })
        .eq('is_resolved', false);

      setActionCounts({
        pendingGames: pendingGames || 0,
        urgentTickets: urgentTickets || 0,
        failedPayments: failedPayments || 0,
        unresolvedAlerts: unresolvedAlerts || 0
      });
    } catch (error) {
      console.error('Error fetching action counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyPause = async () => {
    if (confirm('This will pause all billing and send a maintenance notice. Continue?')) {
      try {
        await createSystemAlert(
          'warning',
          'Emergency Maintenance Mode',
          'Platform temporarily paused for emergency maintenance',
          { emergency: true, paused_by: 'admin' }
        );
        alert('Emergency pause activated. All billing paused and users notified.');
      } catch (error) {
        console.error('Error activating emergency pause:', error);
        alert('Failed to activate emergency pause');
      }
    }
  };

  const handleExportAnalytics = async () => {
    try {
      // This would typically generate and download a report
      const now = new Date();
      const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      // For now, we'll create a simple CSV export
      const csvData = `Report Generated: ${now.toISOString()}\n` +
                     `Period: ${monthYear}\n` +
                     `Active Subscriptions: ${actionCounts.activeSubscriptions || 0}\n` +
                     `Failed Payments: ${actionCounts.failedPayments || 0}\n` +
                     `Pending Games: ${actionCounts.pendingGames || 0}\n`;
      
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solis-games-report-${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting analytics:', error);
      alert('Failed to export analytics');
    }
  };

  const quickActions = [
    {
      id: 1,
      title: 'Approve Pending Games',
      description: `${actionCounts.pendingGames || 0} games awaiting review`,
      icon: 'CheckCircle',
      color: 'accent',
      count: actionCounts.pendingGames,
      action: () => navigate('/admin-studio-management?tab=pending-games'),
      urgent: actionCounts.pendingGames > 5
    },
    {
      id: 2,
      title: 'Review Failed Payments',
      description: `${actionCounts.failedPayments || 0} payment issues`,
      icon: 'CreditCard',
      color: actionCounts.failedPayments > 0 ? 'destructive' : 'primary',
      count: actionCounts.failedPayments,
      action: () => navigate('/admin-user-management?filter=payment-issues'),
      urgent: actionCounts.failedPayments > 3
    },
    {
      id: 3,
      title: 'Handle Urgent Tickets',
      description: `${actionCounts.urgentTickets || 0} urgent support tickets`,
      icon: 'AlertTriangle',
      color: actionCounts.urgentTickets > 0 ? 'destructive' : 'secondary',
      count: actionCounts.urgentTickets,
      action: () => navigate('/admin-contact-management?priority=urgent'),
      urgent: actionCounts.urgentTickets > 0
    },
    {
      id: 4,
      title: 'Resolve System Alerts',
      description: `${actionCounts.unresolvedAlerts || 0} unresolved alerts`,
      icon: 'Bell',
      color: actionCounts.unresolvedAlerts > 0 ? 'destructive' : 'accent',
      count: actionCounts.unresolvedAlerts,
      action: () => window.location.hash = 'system-alerts',
      urgent: actionCounts.unresolvedAlerts > 2
    },
    {
      id: 5,
      title: 'Emergency Pause',
      description: 'Pause billing & notify users',
      icon: 'Pause',
      color: 'destructive',
      action: handleEmergencyPause,
      emergency: true
    },
    {
      id: 6,
      title: 'Export Analytics',
      description: 'Generate monthly report',
      icon: 'Download',
      color: 'muted',
      action: handleExportAnalytics
    }
  ];

  const getActionColor = (color) => {
    switch (color) {
      case 'accent': return 'text-accent';
      case 'warning': return 'text-warning';
      case 'primary': return 'text-primary';
      case 'secondary': return 'text-secondary';
      default: return 'text-muted-foreground';
    }
  };

  const getActionBg = (color) => {
    switch (color) {
      case 'accent': return 'bg-accent/10 hover:bg-accent/20';
      case 'warning': return 'bg-warning/10 hover:bg-warning/20';
      case 'primary': return 'bg-primary/10 hover:bg-primary/20';
      case 'secondary': return 'bg-secondary/10 hover:bg-secondary/20';
      default: return 'bg-muted/10 hover:bg-muted/20';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
          <p className="text-sm text-muted-foreground">Common administrative tasks</p>
        </div>
        
        <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quickActions.map((action) => (
          <button
            key={action.id}
            onClick={action.action}
            className={`flex items-center space-x-4 p-4 rounded-lg border border-border transition-all hover:shadow-gaming ${getActionBg(action.color)}`}
          >
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getActionBg(action.color)}`}>
              <Icon name={action.icon} size={24} className={getActionColor(action.color)} />
            </div>
            
            <div className="flex-1 text-left">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium text-foreground">
                  {action.title}
                </h4>
                {action.count && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    action.color === 'warning' ? 'bg-warning/20 text-warning' :
                    action.color === 'accent'? 'bg-accent/20 text-accent' : 'bg-primary/20 text-primary'
                  }`}>
                    {action.count}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {action.description}
              </p>
            </div>
            
            <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
          </button>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleTimeString()}
          </span>
          <Button variant="outline" size="sm" iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;
